/**
 * SchedulesDrawer — upcoming games list with date, opponent, court, RSVP.
 * Theme-aware: uses Tailwind dark: variants throughout.
 */
import React from "react";
import { motion } from "motion/react";
import { Calendar, MapPin, ChevronRight, Settings2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DrawerShell from "./DrawerShell";

// ── Format helpers ────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "TBD";
  // game_date may be a full ISO datetime ("2025-10-15T00:00:00+00:00") or a plain
  // date string ("2025-10-15"). Slice to 10 chars to get "YYYY-MM-DD" before
  // appending the noon-time suffix so the Date constructor never sees a double-T.
  const datePart = String(dateStr).slice(0, 10);
  return new Date(datePart + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

function TimeDisplay(dateStr) {
  // 1. Safe guard against null, undefined, or empty values
  console.log(dateStr);

  if (!dateStr || typeof dateStr !== "string") return "TBD";

  const parts = dateStr.split("T");

  // 2. Ensure the string actually has a time part after the space
  if (parts.length < 2) return "TBD";
  const timePart = parts[1]; // Example: "20:20:00"

  // 3. Extract hours and minutes safely
  const timeSegments = timePart.split(":");
  if (timeSegments.length < 2) return "TBD";
  let [hours, minutes] = timeSegments;

  // 4. Convert 24-hour string to 12-hour AM/PM
  hours = parseInt(hours, 10);
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

// games table uses team1_id / team2_id joined as team1 / team2
const isMyGame = (game, teamId) =>
  teamId && (game.team1?.id === teamId || game.team2?.id === teamId);

// ── Game row ──────────────────────────────────────────────────────────────────
const GameRow = ({ game, teamId, rsvpStatus, onRsvp, index }) => {
  const mine = isMyGame(game, teamId);
  const isTeam1 = game.team1?.id === teamId;
  const opponent = isTeam1 ? game.team2 : game.team1;
  const vsLabel = isTeam1 ? "vs" : "@";
  const venue = game.court || null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: index * 0.045,
        type: "spring",
        stiffness: 380,
        damping: 32,
      }}
      className={`mx-5 mb-2.5 rounded-2xl border p-4 ${
        mine
          ? "bg-orange-50 dark:bg-gray-900 border-orange-200 dark:border-orange-500/20"
          : "bg-gray-50 dark:bg-gray-900/60 border-gray-100 dark:border-white/[0.06]"
      }`}
    >
      {/* Date + time row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar
            size={12}
            className="text-orange-500 dark:text-orange-400 flex-shrink-0"
          />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {formatDate(game.game_date)}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
        </div>
        {mine && (
          <span
            className="text-[9px] font-black tracking-widest uppercase
                           text-orange-600 dark:text-orange-400
                           bg-orange-100 dark:bg-orange-500/15
                           px-2 py-0.5 rounded-full"
          >
            My Game
          </span>
        )}
      </div>

      {/* Matchup */}
      <p className="text-sm font-black text-gray-900 dark:text-white mb-1.5">
        {mine ? (
          <>
            <span className="text-gray-400 dark:text-gray-400 font-semibold">
              {vsLabel}{" "}
            </span>
            {opponent?.name || "TBD"}
          </>
        ) : (
          <>
            <span className="text-gray-700 dark:text-gray-300">
              {game.team1?.name || "TBD"}
            </span>
            <span className="text-gray-400 dark:text-gray-600 font-normal mx-2">
              vs
            </span>
            <span className="text-gray-700 dark:text-gray-300">
              {game.team2?.name || "TBD"}
            </span>
          </>
        )}
      </p>

      {/* Venue + RSVP */}
      <div className="flex items-center justify-between">
        {venue ? (
          <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <MapPin size={10} />
            {venue}
          </span>
        ) : (
          <span className="text-xs text-gray-300 dark:text-gray-600">
            Court TBD
          </span>
        )}
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
          {TimeDisplay(game.game_date)}
        </span>
      </div>
    </motion.div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const SchedulesDrawer = ({
  open,
  onClose,
  upcomingGames,
  teamId,
  rsvpMap,
  onRsvp,
  isAdmin,
  isCaptain,
}) => {
  const navigate = useNavigate();

  const handleNav = (path) => {
    onClose();
    navigate(path);
  };

  const myGames = upcomingGames.filter((g) => isMyGame(g, teamId));
  const otherGames = upcomingGames.filter((g) => !isMyGame(g, teamId));

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Schedule"
      subtitle={`${upcomingGames.length} upcoming game${upcomingGames.length !== 1 ? "s" : ""}`}
    >
      <div className="pb-4">
        {/* Admin shortcut */}
        {isAdmin && (
          <div className="mx-5 mt-4 mb-3">
            <button
              onClick={() => handleNav("/admin?tab=schedule")}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl
                         bg-red-50 dark:bg-red-500/10
                         border border-red-200 dark:border-red-500/25
                         hover:bg-red-100 dark:hover:bg-red-500/15
                         transition-all group"
            >
              <Settings2
                size={15}
                className="text-red-500 dark:text-red-400 flex-shrink-0"
              />
              <span className="text-sm font-bold text-red-700 dark:text-red-300 group-hover:text-red-800 dark:group-hover:text-red-200 transition-colors">
                Manage Schedule
              </span>
              <ChevronRight
                size={14}
                className="text-red-300 dark:text-red-600 ml-auto group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
              />
            </button>
          </div>
        )}

        {upcomingGames.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
            <Calendar
              size={40}
              className="text-gray-300 dark:text-gray-700 mb-3"
            />
            <p className="text-gray-500 dark:text-gray-500 text-sm font-medium">
              No upcoming games scheduled.
            </p>
          </div>
        ) : (
          <>
            {/* My games first */}
            {myGames.length > 0 && (
              <>
                <p className="mx-5 mt-4 mb-2 text-[10px] font-black tracking-[0.18em] uppercase text-orange-500 dark:text-orange-400">
                  My Games
                </p>
                {myGames.map((game, i) => (
                  <GameRow
                    key={game.id}
                    game={game}
                    teamId={teamId}
                    rsvpStatus={rsvpMap[game.id] ?? null}
                    onRsvp={onRsvp}
                    index={i}
                  />
                ))}
              </>
            )}

            {/* All other games */}
            {otherGames.length > 0 && (
              <>
                <p className="mx-5 mt-4 mb-2 text-[10px] font-black tracking-[0.18em] uppercase text-gray-400 dark:text-gray-500">
                  All Games
                </p>
                {otherGames.map((game, i) => (
                  <GameRow
                    key={game.id}
                    game={game}
                    teamId={teamId}
                    rsvpStatus={rsvpMap[game.id] ?? null}
                    onRsvp={onRsvp}
                    index={myGames.length + i}
                  />
                ))}
              </>
            )}

            {/* View full schedule */}
            <div className="mx-5 mt-3">
              <button
                onClick={() => handleNav("/schedule")}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                           bg-gray-100 dark:bg-white/[0.04]
                           border border-gray-200 dark:border-white/[0.07]
                           hover:bg-gray-200 dark:hover:bg-white/[0.08]
                           transition-colors text-sm font-semibold
                           text-gray-500 dark:text-gray-400
                           hover:text-gray-800 dark:hover:text-white"
              >
                View Full Schedule
                <ChevronRight size={14} />
              </button>
            </div>
          </>
        )}
      </div>
    </DrawerShell>
  );
};

export default SchedulesDrawer;
